import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  VStack,
  HStack,
  Icon,
  Switch,
  Link,
  Spinner,
  Button
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import Card from 'components/card/Card';
import PdfIcon from '../../../../assets/img/pdf-icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserDetails, clearSelectedUser, toggleUserStatus, verifyUserDocuments } from '../../../../features/users/actions';
import { useToastNotification } from 'utils/toast-utils';
import DocumentVerificationModal from 'components/modals/DocumentVerificationModal';
import DocumentViewerModal from 'components/modals/DocumentViewerModal';


const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'green.500';
    case 'pending':
      return 'gray.500';
    case 'inactive':
      return 'orange.500';
    default:
      return 'gray.500';
  }
};

const getBackgroundColor = (status) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'green.100';
    case 'pending':
      return 'gray.100';
    case 'inactive':
      return 'orange.100';
    default:
      return 'gray.100';
  }
};

const getFileType = (url) => {
  if (!url) return 'unknown';
  const extension = url.split('.').pop().toLowerCase();
  if (['pdf'].includes(extension)) return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image';
  return 'unknown';
};

const getFileNameFromUrl = (url) => {
  try {
    if (!url) return 'Unknown File';
    
    // Decode URL to handle special characters
    const decodedUrl = decodeURIComponent(url);
    
    // Extract filename from URL
    const fileName = decodedUrl.split('/').pop();
    
    // Remove any query parameters if present
    const cleanFileName = fileName.split('?')[0];
    
    // Handle empty or invalid filenames
    return cleanFileName || 'Unknown File';
  } catch (error) {
    console.error('Error extracting filename:', error);
    return 'Unknown File';
  }
};

const UserDetails = ({ userData, onBack }) => {
  const dispatch = useDispatch();
  const { selectedUser, loading, error } = useSelector((state) => state.users);
  const { showToast } = useToastNotification();
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  useEffect(() => {
    if (userData?._id) {
      dispatch(fetchUserDetails(userData._id));
    }
    
    return () => {
      dispatch(clearSelectedUser());
    };
  }, [dispatch, userData?._id]);

  const handleStatusToggle = async (event) => {
    try {
      const activate = event.target.checked;
      await dispatch(toggleUserStatus({ 
        userId: userData._id, 
        activate 
      })).unwrap();
      
      showToast(
        'Success',
        `User ${activate ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
    } catch (error) {
      showToast(
        'Error',
        error.message || 'Failed to update user status',
        'error'
      );
    }
  };

  const handleVerifyDocuments = async (verificationData) => {
    try {
      await dispatch(verifyUserDocuments({ 
        userId: userData._id, 
        verificationData 
      })).unwrap();
      
      showToast(
        'Success',
        `Documents ${verificationData.status.toLowerCase()} successfully`,
        'success'
      );
      setIsVerificationModalOpen(false);
    } catch (error) {
      showToast(
        'Error',
        error.message || 'Failed to verify documents',
        'error'
      );
    }
  };

  const handleDocumentClick = (doc) => {
    setSelectedDocument({
      ...doc,
      fileType: getFileType(doc.url)
    });
    setIsViewerOpen(true);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="xl" color="purple.500" />
      </Flex>
    );
  }

  if (error && !selectedUser) {
    return (
      <Box p="4">
        <Text color="red.500">Error: {error}</Text>
        <Button mt="4" onClick={onBack}>Go Back</Button>
      </Box>
    );
  }

  // Use selectedUser instead of userData for rendering
  const user = selectedUser || userData;

  return (
    <Box p="0px">
      <Card p="0px">
        <Flex direction="column" gap={4}>
          {/* Header */}
          <Flex align="center">
            <Icon
              as={FiArrowLeft}
              w={6}
              h={6}
              cursor="pointer"
              onClick={onBack}
              mr={4}
            />
            <Box>
              <Text fontSize="2xl" fontWeight="bold">User Details</Text>
              <Flex align="center">
                <Text color="gray.500">{user?.email}</Text>
                <Box fontSize="sm"

                  px={3}
                  py={1}
                  borderRadius="full"
                  width="fit-content" ml={2} backgroundColor={getBackgroundColor(user?.status)} color={getStatusColor(user?.status)}>
                  <Text fontWeight="500">{user?.status}</Text>
                </Box>
              </Flex>
            </Box>

            <Flex ml="auto" align="center">
              <Text mr={3}>Activate</Text>
              <Switch
                isChecked={user?.status?.toLowerCase() === 'active'}
                colorScheme="purple"
                onChange={handleStatusToggle}
                isDisabled={loading}
              />
            </Flex>
          </Flex>

          {/* Company Info */}
          <Card p="0px">
            <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={4}>Company Info</Text>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel color="#42505C" fontSize="12px"
                    fontWeight="400">Company Name</FormLabel>
                  <Input
                    value={user?.companyName || ''}
                    isReadOnly
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="4px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color="#42505C" fontSize="12px"
                    fontWeight="400">Phone number</FormLabel>
                  <Input
                    value={user?.companyPhoneNumber || ''}
                    isReadOnly
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="4px"
                  />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel color="#42505C" fontSize="12px"
                  fontWeight="400">RIF (Tax Identification Number)</FormLabel>
                <Input
                  value={user?.rif || '00000'}
                  isReadOnly
                  bg="white"
                  border="1px solid #D1D1D1"
                  height="48px"
                  w="100%"
                  borderRadius="4px"
                />
              </FormControl>
            </VStack>
          </Card>

          {/* Contact Person */}
          <Card p="0px">
            <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={4}>Contact Person</Text>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel color="#42505C" fontSize="12px"
                    fontWeight="400">Name</FormLabel>
                  <Input
                    value={user?.contactPersons?.[0]?.name || ''}
                    isReadOnly
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="4px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color="#42505C" fontSize="12px"
                    fontWeight="400">Phone number</FormLabel>
                  <Input
                    value={user?.contactPersons?.[0]?.phoneNumber || ''}
                    isReadOnly
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="4px"
                  />
                </FormControl>
              </HStack>
            </VStack>
          </Card>

          {/* Bank Details */}
          <Card p="0px">
            <Text fontSize="1rem" fontWeight="600" color="#42505C" mb={4}>Bank Details</Text>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel color="#42505C" fontSize="12px"
                    fontWeight="400">Bank Name</FormLabel>
                  <Input
                    value={user?.bankInformation?.accountName || ''}
                    isReadOnly
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="4px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color="#42505C" fontSize="12px"
                    fontWeight="400">Bank Account Number</FormLabel>
                  <Input
                    value={user?.bankInformation?.accountNumber || ''}
                    isReadOnly
                    bg="white"
                    border="1px solid #D1D1D1"
                    height="48px"
                    borderRadius="4px"
                  />
                </FormControl>
              </HStack>
            </VStack>
          </Card>

          {/* Support Documents */}
          <Card p="0px">
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="1rem" fontWeight="600" color="#42505C">Support Documents</Text>
              {user?.documents?.length > 0 && (
                user?.documentVerificationStatus === 'APPROVED' ? (
                  <Box
                    px={3}
                    py={1}
                    borderRadius="full"
                    bg="green.100"
                    color="green.500"
                  >
                    <Text fontSize="sm" fontWeight="500">
                      Verified
                    </Text>
                  </Box>
                ) : (
                  <Button
                    colorScheme="purple"
                    size="sm"
                    onClick={() => setIsVerificationModalOpen(true)}
                  >
                    Verify Documents
                  </Button>
                )
              )}
            </Flex>
            <VStack spacing={4} align="stretch">
              {user?.documents?.map((doc, index) => (
                <Flex
                  key={index}
                  p={4}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  align="center"
                  cursor="pointer"
                  onClick={() => handleDocumentClick(doc)}
                  _hover={{ bg: 'gray.50' }}
                >
                  {doc.fileType === 'pdf' ? (
                    <Box
                      w="40px"
                      h="40px"
                      mr={4}
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                    >
                      <img src={PdfIcon} alt="PDF Document" style={{ width: '34px' }} />
                    </Box>
                  ) : (
                    <Box
                      w="40px"
                      h="40px"
                      borderRadius="md"
                      overflow="hidden"
                      mr={4}
                      flexShrink={0}
                    >
                      <img
                        src={doc.url}
                        alt={doc.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                  <Box flex="1">
                    <Text fontWeight="500" fontSize="16px" color="#4E4E4E">
                    {doc.name} - {getFileNameFromUrl(doc.url)}
                    </Text>
                    <Text fontWeight="500" fontSize="12px" color="#D1D1D1">
                      Uploaded {doc.uploadDate}
                    </Text>
                  </Box>
                  {doc.verificationStatus && (
                    <Box
                      ml="auto"
                      px={3}
                      py={1}
                      borderRadius="full"
                      bg={doc.verificationStatus === 'APPROVED' ? 'green.100' : 'red.100'}
                      color={doc.verificationStatus === 'APPROVED' ? 'green.500' : 'red.500'}
                    >
                      <Text fontSize="sm" fontWeight="500">
                        {doc.verificationStatus}
                      </Text>
                    </Box>
                  )}
                </Flex>
              ))}
            </VStack>
          </Card>
        </Flex>
      </Card>

      {/* Add the DocumentVerificationModal */}
      <DocumentVerificationModal
        isOpen={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        onVerify={handleVerifyDocuments}
      />

      <DocumentViewerModal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        document={selectedDocument}
      />
    </Box>
  );
};

export default UserDetails;
